/**
 * ListView — virtualised table view of cards for one board.
 *
 * Composition:
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Header (filters · grouping · saved views)                │
 *   ├──────────────────────────────────────────────────────────┤
 *   │ Bulk-actions bar (visible only when ≥1 row selected)     │
 *   ├──────────────────────────────────────────────────────────┤
 *   │ Sticky sort header                                       │
 *   │ Virtualised rows (TanStack Virtual)                      │
 *   └──────────────────────────────────────────────────────────┘
 *
 * The component is stateful — it owns the local UI state (filters /
 * sort / grouping / selection). Persistence is opt-in via the
 * ``initialSettings`` + ``onSettingsChange`` callback pair (the
 * board page wires them to ``user_view_preferences``).
 */
import type { Card } from '@chaos/types/kanban';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Inbox } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib/utils';
import { ListBulkActions } from './list-bulk-actions';
import { HEADER_ROW_HEIGHT, STATIC_ROW_HEIGHT, visibleColumnDefs } from './list-columns';
import { ListFilters } from './list-filters';
import { groupCards, ListGrouping } from './list-grouping';
import { ListRow } from './list-row';
import { ListSortControls } from './list-sort-controls';
import {
  type CardListColumnId,
  type CardListFilters,
  type CardListGrouping,
  type CardListSort,
  DEFAULT_VISIBLE_COLUMNS,
  type SavedListView,
} from './list-types';
import { SavedViews } from './saved-views';

const GROUP_HEADER_HEIGHT = 28;

export interface ListViewProps {
  cards: Card[];
  isLoading?: boolean;
  onOpenCard?: (id: string) => void;
  onBulkMove?: (cardIds: string[]) => void;
  onBulkArchive?: (cardIds: string[]) => void;
  onBulkAssignAgent?: (cardIds: string[]) => void;
  onBulkSetAutonomy?: (cardIds: string[]) => void;
  visibleColumns?: readonly CardListColumnId[];
  /** Persisted starting state — caller hydrates from the
   *  user_view_preferences row, then writes back via onSettingsChange. */
  initialSettings?: {
    filters?: CardListFilters;
    sorts?: CardListSort[];
    grouping?: CardListGrouping;
    savedViews?: SavedListView[];
    activeSavedViewId?: string | null;
  };
  onSettingsChange?: (settings: {
    filters: CardListFilters;
    sorts: CardListSort[];
    grouping: CardListGrouping;
    savedViews: SavedListView[];
    activeSavedViewId: string | null;
  }) => void;
  className?: string;
}

export function ListView({
  cards,
  isLoading,
  onOpenCard,
  onBulkMove,
  onBulkArchive,
  onBulkAssignAgent,
  onBulkSetAutonomy,
  visibleColumns = DEFAULT_VISIBLE_COLUMNS,
  initialSettings,
  onSettingsChange,
  className,
}: ListViewProps) {
  const [filters, setFilters] = React.useState<CardListFilters>(initialSettings?.filters ?? {});
  const [sorts, setSorts] = React.useState<CardListSort[]>(
    initialSettings?.sorts ?? [{ column: 'last_activity', dir: 'desc' }],
  );
  const [grouping, setGrouping] = React.useState<CardListGrouping>(
    initialSettings?.grouping ?? { by: null },
  );
  const [savedViews, setSavedViews] = React.useState<SavedListView[]>(
    initialSettings?.savedViews ?? [],
  );
  const [activeSavedViewId, setActiveSavedViewId] = React.useState<string | null>(
    initialSettings?.activeSavedViewId ?? null,
  );
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  // Persistence: notify caller after every state change.
  React.useEffect(() => {
    onSettingsChange?.({
      filters,
      sorts,
      grouping,
      savedViews,
      activeSavedViewId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sorts, grouping, savedViews, activeSavedViewId, onSettingsChange]);

  const columns = React.useMemo(() => visibleColumnDefs(visibleColumns), [visibleColumns]);

  const filtered = React.useMemo(() => applyFilters(cards, filters), [cards, filters]);
  const sorted = React.useMemo(
    () => applySorts(filtered, sorts, columns),
    [filtered, sorts, columns],
  );
  const groups = React.useMemo(() => groupCards(sorted, grouping), [sorted, grouping]);

  type Entry =
    | { kind: 'group'; key: string; label: string; count: number }
    | { kind: 'row'; card: Card };
  const flat: Entry[] = React.useMemo(() => {
    const out: Entry[] = [];
    const omitGroup = groups.length === 1 && groups[0]?.key === '__all';
    for (const g of groups) {
      if (!omitGroup) {
        out.push({ kind: 'group', key: g.key, label: g.label, count: g.cards.length });
      }
      for (const c of g.cards) out.push({ kind: 'row', card: c });
    }
    return out;
  }, [groups]);

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const virtualiser = useVirtualizer({
    count: flat.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (flat[i]?.kind === 'group' ? GROUP_HEADER_HEIGHT : STATIC_ROW_HEIGHT),
    overscan: 12,
  });

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    if (selected.size === sorted.length) setSelected(new Set());
    else setSelected(new Set(sorted.map((c) => c.id)));
  }

  function clearSelection() {
    setSelected(new Set());
  }

  function runBulk(action: 'move' | 'archive' | 'assign' | 'autonomy') {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (action === 'move') onBulkMove?.(ids);
    if (action === 'archive') onBulkArchive?.(ids);
    if (action === 'assign') onBulkAssignAgent?.(ids);
    if (action === 'autonomy') onBulkSetAutonomy?.(ids);
    clearSelection();
  }

  function saveCurrentView(name: string) {
    const id = `view-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const created: SavedListView = {
      id,
      name,
      filters,
      sort: sorts[0] ?? null,
      grouping,
      visible_columns: [...visibleColumns],
      created_at: new Date().toISOString(),
      shared_with: 'me',
    };
    setSavedViews((prev) => [...prev, created]);
    setActiveSavedViewId(id);
  }

  function selectSavedView(id: string | null) {
    setActiveSavedViewId(id);
    if (id === null) {
      setFilters({});
      setSorts([{ column: 'last_activity', dir: 'desc' }]);
      setGrouping({ by: null });
      return;
    }
    const view = savedViews.find((v) => v.id === id);
    if (!view) return;
    setFilters(view.filters);
    setSorts(view.sort ? [view.sort] : []);
    setGrouping(view.grouping);
  }

  function deleteSavedView(id: string) {
    setSavedViews((prev) => prev.filter((v) => v.id !== id));
    if (activeSavedViewId === id) setActiveSavedViewId(null);
  }

  const isEmpty = !isLoading && sorted.length === 0;
  const totalHeight = virtualiser.getTotalSize();

  return (
    <section
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden border border-border bg-background',
        className,
      )}
      aria-label="Card list view"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border bg-background px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {sorted.length} {sorted.length === 1 ? 'card' : 'cards'}
          </span>
          {Object.keys(filters).length > 0 && (
            <span className="text-2xs text-muted-foreground">filtered from {cards.length}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ListGrouping value={grouping} onChange={setGrouping} />
          <SavedViews
            views={savedViews}
            activeViewId={activeSavedViewId}
            onSelect={selectSavedView}
            onSaveCurrent={saveCurrentView}
            onDelete={deleteSavedView}
          />
        </div>
      </header>

      <ListFilters value={filters} onChange={setFilters} />

      <ListBulkActions
        selectedCount={selected.size}
        onClear={clearSelection}
        onMove={onBulkMove ? () => runBulk('move') : undefined}
        onArchive={onBulkArchive ? () => runBulk('archive') : undefined}
        onAssignAgent={onBulkAssignAgent ? () => runBulk('assign') : undefined}
        onSetAutonomy={onBulkSetAutonomy ? () => runBulk('autonomy') : undefined}
      />

      <div ref={scrollRef} className="relative flex-1 overflow-auto">
        <ListSortControls
          columns={columns}
          sorts={sorts}
          onChange={setSorts}
          selectedCount={selected.size}
          totalRowCount={sorted.length}
          onToggleSelectAll={selectAll}
        />
        {isEmpty ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 p-8 text-center text-muted-foreground">
            <Inbox size={20} aria-hidden />
            <p className="text-sm">No cards match your filters.</p>
          </div>
        ) : (
          <div
            style={{
              height: `${totalHeight}px`,
              width: '100%',
              position: 'relative',
            }}
            role="grid"
            aria-rowcount={sorted.length}
          >
            {virtualiser.getVirtualItems().map((vrow) => {
              const entry = flat[vrow.index];
              if (!entry) return null;
              const style: React.CSSProperties = {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${vrow.start}px)`,
              };
              if (entry.kind === 'group') {
                return (
                  <div
                    key={entry.key}
                    style={style}
                    role="row"
                    className="flex items-center gap-2 border-b border-border bg-foreground/[0.03] px-3 text-2xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    <div className="flex h-7 items-center gap-2">
                      <span>{entry.label}</span>
                      <span>·</span>
                      <span>{entry.count}</span>
                    </div>
                  </div>
                );
              }
              return (
                <div key={entry.card.id} style={style}>
                  <ListRow
                    card={entry.card}
                    columns={columns}
                    selected={selected.has(entry.card.id)}
                    onToggleSelect={toggleSelect}
                    onOpen={onOpenCard}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── filtering / sorting helpers ─────────────────────────────────────

function applyFilters(cards: readonly Card[], filters: CardListFilters): Card[] {
  return cards.filter((c) => {
    if (filters.search) {
      const needle = filters.search.toLowerCase();
      if (!c.title.toLowerCase().includes(needle)) return false;
    }
    if (filters.autonomy && filters.autonomy.length > 0) {
      if (!c.autonomyLevel) return false;
      if (!filters.autonomy.includes(c.autonomyLevel)) return false;
    }
    if (filters.due_after && c.dueDate) {
      if (new Date(c.dueDate) < new Date(filters.due_after)) return false;
    }
    if (filters.due_before && c.dueDate) {
      if (new Date(c.dueDate) > new Date(filters.due_before)) return false;
    }
    if (filters.due_after && !c.dueDate) return false;
    if (filters.due_before && !c.dueDate) return false;
    if (filters.agent && filters.agent.length > 0) {
      if (!c.primaryAgentId || !filters.agent.includes(c.primaryAgentId)) return false;
    }
    return true;
  });
}

function applySorts(
  cards: readonly Card[],
  sorts: readonly CardListSort[],
  columns: ReturnType<typeof visibleColumnDefs>,
): Card[] {
  if (sorts.length === 0) return [...cards];
  const compareCol = new Map(columns.map((c) => [c.id, c.compare]));
  const out = [...cards];
  out.sort((a, b) => {
    for (const sort of sorts) {
      const cmp = compareCol.get(sort.column);
      if (!cmp) continue;
      const delta = cmp(a, b);
      if (delta !== 0) return sort.dir === 'asc' ? delta : -delta;
    }
    return 0;
  });
  return out;
}

// Re-exports for tests + boards page.
export { applyFilters, applySorts, GROUP_HEADER_HEIGHT, HEADER_ROW_HEIGHT };
